/**
 * Folder permission editing shared by the admin public folders and the webmail calendar sharing: Exchange role names,
 * the individual-rights checklist and the "Custom…" dialog. Bit values and role presets come from the platform API
 * (lib/folder-rights.ts), never from the client; labels from the `common.folderRole/folderRight` keys.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { useId, useState } from 'react';
import { Button } from '../components/button';
import { Checkbox } from '../components/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/dialog';
import { Label } from '../components/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/tabs';

/** Role → label key (unknown roles fall back to "Custom…"). */
export function useFolderRoleLabel(): (role: string) => string {
  const { t } = useI18n();
  return (role) => t(`common.folderRole.${role}` as MessageKey);
}

/** MenuSelect options for the API's role presets plus "Custom…". */
export function useFolderRoleOptions<R extends string>(roles: readonly { role: R }[]): { value: R | 'custom'; label: string }[] {
  const label = useFolderRoleLabel();
  return [...roles.map((preset) => preset.role), 'custom' as const].map((role) => ({ value: role, label: label(role) }));
}

/** The preset with exactly these rights, else `custom`. */
export function folderRoleFor<R extends string>(roles: readonly { role: R; rights: number }[], rights: number): R | 'custom' {
  return roles.find((preset) => preset.rights === rights)?.role ?? 'custom';
}

export interface FolderRightsChecklistProps {
  /** Bit value per right key (API `rightBits`). */
  rightBits: Readonly<Record<string, number>>;
  /** Rights offered, in order (API `customRights`). */
  keys: readonly string[];
  rights: number;
  onChange: (rights: number) => void;
  /** Legend of the group (default: "Individual rights"). */
  legend?: string;
}

/** Checkbox per individual right; every change reports the new bit mask. */
export function FolderRightsChecklist({ rightBits, keys, rights, onChange, legend }: FolderRightsChecklistProps) {
  const { t } = useI18n();
  const prefix = useId();
  const controls = (subset: readonly string[]) => <div className="grid gap-2 sm:grid-cols-2">{subset.map(key => {
    const bit = rightBits[key] ?? 0; const id = `${prefix}-${key}`;
    return <div key={key} className="flex items-center gap-2"><Checkbox id={id} checked={(rights & bit) !== 0} onCheckedChange={checked => onChange(checked === true ? rights | bit : rights & ~bit)} /><Label htmlFor={id} className="font-normal">{t(`common.folderRight.${key}` as MessageKey)}</Label></div>;
  })}</div>;
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">{legend ?? t('common.folderRight.legend')}</legend>
      {keys.length <= 8 ? controls(keys) : <Tabs defaultValue="items"><TabsList aria-label={legend ?? t('common.folderRight.legend')}><TabsTrigger value="items">{t('common.folderRight.itemsTab')}</TabsTrigger><TabsTrigger value="folder">{t('common.folderRight.folderTab')}</TabsTrigger></TabsList><TabsContent value="items">{controls(keys.slice(0, 6))}</TabsContent><TabsContent value="folder">{controls(keys.slice(6))}</TabsContent></Tabs>}
    </fieldset>
  );
}

export interface CustomRightsDialogProps {
  title: string;
  description: string;
  rightBits: Readonly<Record<string, number>>;
  keys: readonly string[];
  initial: number;
  saveLabel: string;
  onSave: (rights: number) => void | Promise<void>;
  onClose: () => void;
}

/** "Custom…": edit the individual rights of one principal. */
export function CustomRightsDialog({ title, description, rightBits, keys, initial, saveLabel, onSave, onClose }: CustomRightsDialogProps) {
  const { t } = useI18n();
  const [rights, setRights] = useState(initial);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent framed>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody><FolderRightsChecklist rightBits={rightBits} keys={keys} rights={rights} onChange={setRights} /></DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void Promise.resolve(onSave(rights)).finally(() => setBusy(false));
            }}
          >
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
